<template>
  <div class="roles-tab">
    <div class="roles-tab__header">
      <div v-if="isRecentlyAdded" class="roles-tab__header_recently-added">
        {{ recentlyAddedTitle }}
      </div>
      <button class="roles-tab__button" @click.prevent="openAddNewModal">
        <base-material-design-icon class="roles-tab__button__plus" icon="plus"/>
        {{ addNewTitle }}
      </button>
    </div>
    <div class="common-table roles-tab__table">
      <div class="common-table__row common-table__header">
        <div class="common-table__header-cell"
             v-for="column in tableConfig"
             :key="column.property"
             :class="[`roles-tab__${column.property}`]">
          <base-multi-select-sortable-header
            v-if="column.property !== 'cross'"
            class="roles-tab__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
          />
          <!--     Move headers according columns     -->
          <div v-else style="width: 88px"></div>
        </div>
      </div>

      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          {{ missingDataTitle }}
        </div>
      </template>
      <template v-else>
        <template v-for="data in rolesTabData">
          <div :key="data.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in tableConfig"
                 :key="column.property"
                 :class="[`roles-tab__row_${column.property}`]">
              <template v-if="column.isChangeable">
                <base-select
                         class="roles-tab__select"
                         alignLeft
                         borderLess
                         :options="column.options"
                         :disabled="disableSelects.includes(data.id)"
                         :selectStyle="{ width: '200px', height: '16px !important' }"
                         v-model="data.role_id"
                         @change="handleRoleChange($event, data.id)"
                />
              </template>
              <div v-else-if="column.property !== 'cross'">
                {{ data[column.property] }}
              </div>
              <template v-else-if="!hiddenCross.includes(data.id)">
                <base-material-design-icon @click="handleRemove(data.id)" class="roles-tab__row-button-cross" icon="plus"/>
              </template>
            </div>
          </div>
        </template>
      </template>

      <div class="pagination-wrapper" v-show="hasPagination">
        <base-pagination :limit="pageItemsLimit"
                         :total-items="totalItems"
                         @pageChanged="pageChanged"/>
      </div>
    </div>
    <modal name="add-new"
           v-bind="modalProps">
      <AddNewModal :modalConfig="modalConfig" @close="closeModal"/>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import helper from '@/services/Helper'
import AddNewModal from './AddNewModal'
import baseModalProps from '@/common/BaseModalProps'

const AGENCY_ROLES_DEFAULT_CONFIG = [
  {
    label: 'Agency',
    property: 'agency',
    isSelectable: true,
    options: []
  },
  {
    label: 'Role',
    property: 'role',
    isSelectable: true,
    isChangeable: true,
    options: []
  },
  {
    property: 'cross'
  }
]
const CLIENT_ROLES_DEFAULT_CONFIG = [
  {
    label: 'Client',
    property: 'client',
    isSelectable: true,
    options: []
  },
  {
    label: 'Agency',
    property: 'agency',
    isSelectable: true,
    options: []
  },
  {
    label: 'Role',
    property: 'role',
    isSelectable: true,
    isChangeable: true,
    options: []
  },
  {
    property: 'cross'
  }
]
const PAGE_ITEMS_LIMIT = 50
const AGENCY_ROLES_DEFAULT_FILTER_PARAMS = {
  agency: [],
  role: [],
  page_from: 0,
  page_to: PAGE_ITEMS_LIMIT
}
const CLIENT_ROLES_DEFAULT_FILTER_PARAMS = {
  agency: [],
  role: [],
  client: [],
  page_from: 0,
  page_to: PAGE_ITEMS_LIMIT
}
const AGENCY_ADD_NEW_CONFIG = [
  {
    label: 'Agency',
    property: 'agency_id',
    isSmartInput: true,
    options: []
  },
  {
    label: 'Role',
    property: 'role_id',
    isSmartInput: false,
    options: []
  }
]
const CLIENT_ADD_NEW_CONFIG = [
  {
    label: 'Client',
    property: 'client_id',
    isSmartInput: true,
    options: []
  },
  {
    label: 'Role',
    property: 'role_id',
    isSmartInput: false,
    options: []
  }
]

export default {
  name: 'RolesTab',
  components: { AddNewModal },
  data () {
    return {
      filterParams: {},
      pageItemsLimit: PAGE_ITEMS_LIMIT,
      hiddenCross: [],
      disableSelects: [],
      sortConfig: []
    }
  },
  watch: {
    isAgencyRolesTab: {
      handler (v) {
        this.filterParams = v ? { ...AGENCY_ROLES_DEFAULT_FILTER_PARAMS } : { ...CLIENT_ROLES_DEFAULT_FILTER_PARAMS }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('userManager', {
      rolesTabData (state) {
        const filter = item => {
          const { agency = [], role = [], client = [] } = this.filterParams
          const agencyFilter = !agency.length || agency.includes(item.agency_id)
          const roleFilter = !role.length || role.includes(item.role_id)
          const clientFilter = (this.isAgencyRolesTab || !client.length) || client.includes(item.client_id)

          return agencyFilter && roleFilter && clientFilter
        }

        const rolesTabData = this.isAgencyRolesTab ? state.agencyRoles : state.clientRoles
        // eslint-disable-next-line camelcase
        const { page_from = 0, page_to = 50 } = this.filterParams
        return helper.sortByMultipleParams(rolesTabData.filter(filter).slice(page_from, page_to), this.sortConfig)
      },
      options (state) {
        return {
          agency: state.agencyOptions,
          clientRoles: state.clientRolesOptions,
          agencyRoles: state.agencyRolesOptions,
          clients: state.clientOptions
        }
      },
      isRecentlyAdded: 'isRecentlyAdded'
    }),
    recentlyAddedTitle () {
      return this.isAgencyRolesTab ? 'Please add Agency role for this user' : 'Please add Client role for this user'
    },
    missingDataTitle () {
      return this.isAgencyRolesTab ? 'There are no Agency roles yet…' : 'There are no Client roles yet…'
    },
    modalProps () {
      const shareModalProps = {
        height: 'auto',
        width: '610px'
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    modalConfig () {
      const config = {
        title: this.isAgencyRolesTab ? 'Add Agency Role' : 'Add Client Role',
        fieldsConfig: this.isAgencyRolesTab
          ? AGENCY_ADD_NEW_CONFIG.map(item => {
            switch (item.property) {
              case 'agency_id':
                item.options = [...this.options.agency]
                break
              case 'role_id':
                item.options = [...this.options.agencyRoles]
            }
            return item
          })
          : CLIENT_ADD_NEW_CONFIG.map(item => {
            switch (item.property) {
              case 'client_id':
                item.options = [...this.options.clients]
                break
              case 'role_id':
                item.options = [...this.options.clientRoles]
            }
            return item
          }),
        type: this.isAgencyRolesTab ? 'agency' : 'client',
        userId: this.$route.params.id
      }
      return config
    },
    totalItems () {
      return this.rolesTabData.length
    },
    hasPagination () {
      return this.totalItems > this.pageItemsLimit
    },
    isAgencyRolesTab () {
      return this.$route.name === 'AgencyRolesTab'
    },
    tableConfig () {
      const tableConfig = this.isAgencyRolesTab ? [...AGENCY_ROLES_DEFAULT_CONFIG] : [...CLIENT_ROLES_DEFAULT_CONFIG]
      tableConfig.forEach(item => {
        if (item.isSelectable) {
          switch (item.property) {
            case 'agency':
              item.options = [...this.options.agency]
              break
            case 'role':
              item.options = this.isAgencyRolesTab ? [...this.options.agencyRoles] : [...this.options.clientRoles]
              break
            case 'client':
              item.options = [...this.options.clients]
              break
          }
        }
      })
      return tableConfig
    },
    dataMissing () {
      return !this.totalItems
    },
    addNewTitle () {
      return this.isAgencyRolesTab ? 'add agency role' : 'add client role'
    }
  },
  methods: {
    ...mapActions('userManager', ['removeUserRole', 'updateUserRole']),
    onHeaderCellClicked (cell, direction) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortByLenght = cell.sortByLenght
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property,
          sortByLenght: cell.sortByLenght
        }]
      }
    },
    openAddNewModal () {
      this.$modal.show('add-new')
    },
    closeModal (isNeedUpdate = true) {
      this.$modal.hide('add-new')

      if (isNeedUpdate) {
        this.$emit('updateData')
      }
    },
    async handleRoleChange (roleId, recordId) {
      this.disableSelects.push(recordId)
      const isRemoved = await this.updateUserRole({
        record_id: recordId,
        role_id: roleId,
        type: this.isAgencyRolesTab ? 'agency' : 'client'
      })

      const disableSelects = [...this.disableSelects]
      const index = disableSelects.indexOf(recordId)
      disableSelects.splice(index, 1)
      this.disableSelects = disableSelects

      if (isRemoved) {
        this.$emit('updateData')
      }
    },
    async handleRemove (id) {
      this.hiddenCross.push(id)
      const isRemoved = await this.removeUserRole({
        id,
        type: this.isAgencyRolesTab ? 'agency' : 'client'
      })

      const hiddenCross = [...this.hiddenCross]
      const index = hiddenCross.indexOf(id)
      hiddenCross.splice(index, 1)
      this.hiddenCross = hiddenCross

      if (isRemoved) {
        this.$emit('updateData')
      }
    },
    filterColumn: function (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    }
  }
}
</script>

<style lang="scss" scoped>
  .pagination-wrapper {
    display: flex;
    height: 60px;
    margin-top: 10px;
    justify-content: center;
    align-items: center;
    border: 1px solid $rfx-light-steel;
    border-radius: 4px;
  }

  .common-table {
    overflow: visible;
    border-radius: 4px;

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 0;
      height: 60px;
      display: flex;
      align-items: center;
      cursor: pointer;

      &:first-child {
        padding-left: 13px;
      }

      //&:last-child {
      //  padding-right: 25px;
      //}
    }

    &__cell {
      color: $rfx-dark-march;
      height: auto;
      padding: 23px 0 23px 13px;
      display: flex;
      align-items: flex-start;
      font-size: 14px;
      border-bottom: 1px solid $rfx-light-steel;

      &:first-child {
        padding-left: 25px;
      }

      &:last-child {
        padding-right: 25px;
      }
    }

    &__row {
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;
    }
  }

  .roles-tab {
    width: 100%;

    &__header {
      display: flex;
      align-items: center;

      &_recently-added {
        font-size: 16px;
        margin-left: 24px;
      }
    }

    &__button {
      width: 175px;
      padding-left: 40px;
      padding-right: 20px;
      height: 40px;
      display: block;
      margin: 16px 16px 16px auto;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: $rfx-medium-march;
      color: #fff;
      position: relative;

      &__plus {
        position: absolute;
        left: 18px;
        top: 12px;
        font-size: 17px;
      }
    }

    &__row-button-cross {
      cursor: pointer;
      font-size: 26px;
      position: absolute;
      transform: rotate(45deg);
    }

    &__row_cross,
    &__cross {
      flex: 0 50px;
      position: relative;
      color: $rfx-dark-march;
    }
  }

  .no-data-block {
    font-size: 14px;
    font-weight: normal;
  }
</style>
